import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Add, ChevronLeft, ChevronRight, Close } from "@material-ui/icons";
import { addSnackbarIssue } from "../../../components/common/StoreCommon";
import {
  apiActionHoliday,
  apiRequestHoliday,
} from "../../../components/common/ApiMiddleware";
import {
  hasConflictingHoliday,
  localDateToUtcIso,
  parseDateInputValue,
  toDateInputValue,
  utcToLocalDate,
} from "../utils/holidayDates";

function formatLocalDate(utcValue) {
  const localDate = utcToLocalDate(utcValue);
  if (!localDate) return "";
  return localDate.toLocaleDateString();
}

function formatLocalDateTime(utcValue) {
  if (!utcValue) return "";
  const date = new Date(utcValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function formatDurationLabel(record) {
  if (record?.durationType === "PartDay") {
    return record.partDayType || "Part Day";
  }
  return "Whole day";
}

function formatUserName(getUser, userId) {
  if (!userId) return "";
  const user = getUser(userId);
  return user ? `${user.FirstName} ${user.LastName}` : `User ${userId}`;
}

function isSameLocalDay(start, end) {
  return (
    !!start &&
    !!end &&
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth() &&
    start.getDate() === end.getDate()
  );
}

function isPendingStatus(status) {
  return String(status || "").toLowerCase() === "pending";
}

export default function HolidayRequestsView({
  holidays = [],
  year,
  onYearChange,
  isHrAdmin,
  getUser,
  loggedInUserId,
  onRequested,
}) {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dayPart, setDayPart] = useState("WholeDay");
  const [comments, setComments] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsRecord, setDetailsRecord] = useState(null);

  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionStatus, setActionStatus] = useState(null);
  const [actionHoliday, setActionHoliday] = useState(null);
  const [actionComments, setActionComments] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionSaving, setActionSaving] = useState(false);

  const isMultiDay = !!startDate && !!endDate && !isSameLocalDay(startDate, endDate);
  const effectiveDayPart = isMultiDay ? "WholeDay" : dayPart;

  const rows = useMemo(() => {
    return [...(holidays || [])]
      .filter((h) => {
        const start = utcToLocalDate(h.startDateTime);
        const end = utcToLocalDate(h.endDateTime);
        if (!start || !end) return false;
        return start.getFullYear() <= year && end.getFullYear() >= year;
      })
      .sort((a, b) => {
        const requestedA = a.requestedDateTime
          ? new Date(a.requestedDateTime).getTime()
          : 0;
        const requestedB = b.requestedDateTime
          ? new Date(b.requestedDateTime).getTime()
          : 0;
        if (requestedA !== requestedB) return requestedB - requestedA;
        return (
          new Date(b.startDateTime).getTime() - new Date(a.startDateTime).getTime()
        );
      });
  }, [holidays, year]);

  const openDialog = () => {
    setStartDate(null);
    setEndDate(null);
    setDayPart("WholeDay");
    setComments("");
    setFormError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setFormError("");
  };

  const openDetails = (record) => {
    setDetailsRecord(record);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsRecord(null);
  };

  const openActionDialog = (holiday, status) => {
    setActionHoliday(holiday);
    setActionStatus(status);
    setActionComments("");
    setActionError("");
    setActionDialogOpen(true);
  };

  const closeActionDialog = () => {
    setActionDialogOpen(false);
    setActionHoliday(null);
    setActionStatus(null);
    setActionComments("");
    setActionError("");
  };

  const applyDateChange = (nextStart, nextEnd) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    if (nextStart && nextEnd && !isSameLocalDay(nextStart, nextEnd)) {
      setDayPart("WholeDay");
    }
    setFormError("");
  };

  const validate = () => {
    if (!startDate) {
      setFormError("Please enter a start date");
      return false;
    }
    if (!endDate) {
      setFormError("Please enter an end date");
      return false;
    }
    if (endDate < startDate) {
      setFormError("End date must be on or after the start date");
      return false;
    }
    if (
      hasConflictingHoliday({
        appUserId: loggedInUserId,
        rangeStart: startDate,
        rangeEnd: endDate,
        holidays,
      })
    ) {
      setFormError(
        "A holiday already exists for you on one or more of these days"
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    setFormError("");
    const durationType =
      effectiveDayPart === "WholeDay" ? "WholeDay" : "PartDay";
    const partDayType =
      effectiveDayPart === "WholeDay" ? null : effectiveDayPart;
    try {
      await apiRequestHoliday({
        startDateTime: localDateToUtcIso(startDate),
        endDateTime: localDateToUtcIso(endDate),
        durationType,
        partDayType,
        comments: comments || null,
      });
      addSnackbarIssue("Holiday request submitted", "success", false, 5000);
      closeDialog();
      if (onRequested) await onRequested();
    } catch (e) {
      setFormError(
        "Unable to submit holiday request. Please check the details and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleActionSubmit = async () => {
    if (!actionHoliday || !actionStatus) return;
    setActionSaving(true);
    setActionError("");
    try {
      await apiActionHoliday({
        holidayId: actionHoliday.id,
        status: actionStatus,
        comments: actionComments || null,
      });
      addSnackbarIssue(
        `Holiday request ${actionStatus.toLowerCase()}`,
        "success",
        false,
        5000
      );
      closeActionDialog();
      if (onRequested) await onRequested();
    } catch (e) {
      setActionError(
        `Unable to ${actionStatus.toLowerCase()} this holiday request. Please try again.`
      );
    } finally {
      setActionSaving(false);
    }
  };

  const dateInputStyle = {
    width: "100%",
    height: 40,
    padding: "8px 12px",
    fontSize: 14,
    border: "1px solid rgba(0,0,0,0.23)",
    borderRadius: 4,
    outline: "none",
    boxSizing: "border-box",
  };

  const colSpan = isHrAdmin ? 7 : 5;

  return (
    <Box className={classes.root}>
      <Box className={classes.toolbar}>
        <Box className={classes.yearNav}>
          <IconButton
            size="small"
            onClick={() => onYearChange(year - 1)}
            aria-label="Previous year"
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" className={classes.yearTitle}>
            {year}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onYearChange(year + 1)}
            aria-label="Next year"
          >
            <ChevronRight />
          </IconButton>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={openDialog}
        >
          Request
        </Button>
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {isHrAdmin ? (
                <TableCell className={classes.headerCell}>User</TableCell>
              ) : null}
              <TableCell className={classes.headerCell}>Start</TableCell>
              <TableCell className={classes.headerCell}>End</TableCell>
              <TableCell className={classes.headerCell}>Status</TableCell>
              <TableCell className={classes.headerCell}>Requested</TableCell>
              <TableCell className={classes.headerCell}>Actioned On</TableCell>
              {isHrAdmin ? (
                <TableCell className={classes.headerCell} align="right" />
              ) : null}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan}>
                  <Typography variant="body2" color="textSecondary">
                    No holiday requests in this year
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((record) => {
                const userName = formatUserName(getUser, record.appUserId);
                const showActions =
                  isHrAdmin && isPendingStatus(record.status);
                return (
                  <TableRow
                    key={record.id}
                    hover
                    className={classes.row}
                    onClick={() => openDetails(record)}
                  >
                    {isHrAdmin ? (
                      <TableCell className={classes.bodyCell}>{userName}</TableCell>
                    ) : null}
                    <TableCell className={classes.bodyCell}>
                      {formatLocalDate(record.startDateTime)}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {formatLocalDate(record.endDateTime)}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {record.status || "Approved"}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {formatLocalDateTime(record.requestedDateTime)}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {formatLocalDateTime(record.actionedDateTime)}
                    </TableCell>
                    {isHrAdmin ? (
                      <TableCell className={classes.bodyCell} align="right">
                        {showActions ? (
                          <Box
                            className={classes.actionButtons}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              onClick={() => openActionDialog(record, "Approved")}
                            >
                              Approve
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              onClick={() => openActionDialog(record, "Declined")}
                            >
                              Decline
                            </Button>
                          </Box>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal open={dialogOpen} onClose={closeDialog} className={classes.modal}>
        <Fade in={dialogOpen}>
          <Paper className={classes.modalPaper}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6">Request holiday</Typography>
              <IconButton size="small" onClick={closeDialog}>
                <Close />
              </IconButton>
            </Box>
            <Box className={classes.modalBody}>
              <Box>
                <Typography variant="caption" className={classes.dateLabel}>
                  Start date
                </Typography>
                <input
                  type="date"
                  value={toDateInputValue(startDate)}
                  onChange={(e) => {
                    const next = parseDateInputValue(e.target.value);
                    applyDateChange(next, !endDate && next ? next : endDate);
                  }}
                  style={dateInputStyle}
                />
              </Box>
              <Box>
                <Typography variant="caption" className={classes.dateLabel}>
                  End date
                </Typography>
                <input
                  type="date"
                  value={toDateInputValue(endDate)}
                  min={toDateInputValue(startDate) || undefined}
                  onChange={(e) => {
                    applyDateChange(startDate, parseDateInputValue(e.target.value));
                  }}
                  style={dateInputStyle}
                />
              </Box>
              <FormControl variant="outlined" fullWidth disabled={isMultiDay}>
                <InputLabel>Duration</InputLabel>
                <Select
                  label="Duration"
                  value={effectiveDayPart}
                  onChange={(e) => {
                    setDayPart(e.target.value);
                    setFormError("");
                  }}
                >
                  <MenuItem value="WholeDay">Whole day</MenuItem>
                  <MenuItem value="AM">AM</MenuItem>
                  <MenuItem value="PM">PM</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Comments"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
              />
              {formError ? (
                <Typography variant="caption" className={classes.error}>
                  {formError}
                </Typography>
              ) : null}
            </Box>
            <Box className={classes.modalActions}>
              <Button onClick={closeDialog} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={saving}
              >
                Submit
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Modal>

      <Modal open={detailsOpen} onClose={closeDetails} className={classes.modal}>
        <Fade in={detailsOpen}>
          <Paper className={classes.modalPaper}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6">Holiday request details</Typography>
              <IconButton size="small" onClick={closeDetails}>
                <Close />
              </IconButton>
            </Box>
            <Box className={classes.modalBody}>
              {isHrAdmin ? (
                <TextField
                  label="User"
                  variant="outlined"
                  fullWidth
                  value={formatUserName(getUser, detailsRecord?.appUserId)}
                  disabled
                />
              ) : null}
              <TextField
                label="Start"
                variant="outlined"
                fullWidth
                value={formatLocalDate(detailsRecord?.startDateTime)}
                disabled
              />
              <TextField
                label="End"
                variant="outlined"
                fullWidth
                value={formatLocalDate(detailsRecord?.endDateTime)}
                disabled
              />
              <TextField
                label="Duration"
                variant="outlined"
                fullWidth
                value={formatDurationLabel(detailsRecord)}
                disabled
              />
              <TextField
                label="Comments"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
                value={detailsRecord?.comments || ""}
                disabled
              />
              <TextField
                label="Status"
                variant="outlined"
                fullWidth
                value={detailsRecord?.status || "Approved"}
                disabled
              />
              <TextField
                label="Requested"
                variant="outlined"
                fullWidth
                value={formatLocalDateTime(detailsRecord?.requestedDateTime)}
                disabled
              />
              <TextField
                label="Actioned By"
                variant="outlined"
                fullWidth
                value={formatUserName(getUser, detailsRecord?.actionedUserId)}
                disabled
              />
              <TextField
                label="Actioned On"
                variant="outlined"
                fullWidth
                value={formatLocalDateTime(detailsRecord?.actionedDateTime)}
                disabled
              />
            </Box>
            <Box className={classes.modalActions}>
              <Button onClick={closeDetails}>Close</Button>
            </Box>
          </Paper>
        </Fade>
      </Modal>

      <Modal
        open={actionDialogOpen}
        onClose={closeActionDialog}
        className={classes.modal}
      >
        <Fade in={actionDialogOpen}>
          <Paper className={classes.modalPaper}>
            <Box className={classes.modalHeader}>
              <Typography variant="h6">
                {actionStatus === "Approved"
                  ? "Approve holiday request"
                  : "Decline holiday request"}
              </Typography>
              <IconButton size="small" onClick={closeActionDialog}>
                <Close />
              </IconButton>
            </Box>
            <Box className={classes.modalBody}>
              <TextField
                label="Comments"
                variant="outlined"
                fullWidth
                multiline
                rows={3}
                value={actionComments}
                onChange={(e) => setActionComments(e.target.value)}
              />
              {actionError ? (
                <Typography variant="caption" className={classes.error}>
                  {actionError}
                </Typography>
              ) : null}
            </Box>
            <Box className={classes.modalActions}>
              <Button onClick={closeActionDialog} disabled={actionSaving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleActionSubmit}
                disabled={actionSaving}
              >
                OK
              </Button>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </Box>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    flexGrow: 1,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  yearNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  yearTitle: {
    fontWeight: 400,
    color: "rgb(100, 100, 100)",
    minWidth: 80,
    textAlign: "center",
  },
  tableContainer: {
    flexGrow: 1,
    border: `1px solid ${theme.palette.divider}`,
    maxHeight: "calc(100vh - 200px)",
  },
  headerCell: {
    fontWeight: 700,
    fontSize: "1.1rem",
    backgroundColor: theme.palette.background.paper,
  },
  bodyCell: {
    fontSize: "1.1rem",
  },
  row: {
    cursor: "pointer",
  },
  actionButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  modalPaper: {
    width: "90%",
    maxWidth: 480,
    padding: theme.spacing(2),
    outline: "none",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  modalBody: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  dateLabel: {
    display: "block",
    marginBottom: 4,
    color: "rgb(100, 100, 100)",
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  error: {
    color: theme.palette.error.main,
  },
}));
